import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Card, Btn } from "./ui";

const SIZES = [10, 20, 50, 100];

const flag = (cc) => (cc && cc.length === 2 ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0))) : "");

const Location = ({ v }) => {
  if (!v.country) return <span className="text-zinc-600">Unknown</span>;
  return (
    <span className="flex items-center gap-2 whitespace-nowrap">
      {v.country_code && <span className="rounded bg-white/5 px-1 font-mono text-[10px] text-[#D4AF37]" aria-hidden>{flag(v.country_code)} {v.country_code}</span>}
      <span className="text-zinc-300">{v.city ? `${v.city}, ` : ""}<span className="text-zinc-500">{v.country}</span></span>
    </span>
  );
};

const Pager = ({ page, pages, total, limit, onPage, onLimit }) => {
  const from = total ? (page - 1) * limit + 1 : 0;
  const to = Math.min(page * limit, total);
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-500" data-testid="visitors-pager">
      <label className="flex items-center gap-2">
        Show
        <select value={limit} onChange={(e) => onLimit(Number(e.target.value))} data-testid="visitors-page-size"
          className="rounded-lg border border-[#D4AF37]/30 bg-[#0B0C10] px-2 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#D4AF37]">
          {SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        per page
      </label>
      <span data-testid="visitors-range-info">{from}–{to} of {total}</span>
      <div className="flex items-center gap-2">
        <Btn variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="visitors-prev"><ChevronLeft size={14} /></Btn>
        <span className="text-zinc-300" data-testid="visitors-page-info">Page {page} / {pages}</span>
        <Btn variant="outline" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="visitors-next"><ChevronRight size={14} /></Btn>
      </div>
    </div>
  );
};

export const RecentVisitors = ({ query }) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setPage(1); }, [query]);

  const load = useCallback((isLive = () => true) => {
    setLoading(true);
    return api.get(`/admin/visitors?${query}&page=${page}&limit=${limit}`)
      .then(({ data }) => isLive() && setD(data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => isLive() && setLoading(false));
  }, [query, page, limit]);

  useEffect(() => {
    let live = true;
    load(() => live);
    return () => { live = false; };
  }, [load]);

  const block = async (ip) => {
    try {
      await api.post("/admin/blocked", { ip, reason: "Blocked from dashboard" });
      toast.success(`${ip} blocked`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const items = d?.items || [];
  return (
    <Card title="Recent visitors" className="mt-6" testid="recent-visitors">
      <div className={`overflow-x-auto transition-opacity ${loading ? "opacity-50" : "opacity-100"}`}>
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="text-xs uppercase tracking-wider text-zinc-500 [&_th]:pb-3 [&_th]:align-bottom"><tr><th>IP</th><th>Location</th><th>Hits</th><th>Device</th><th>Last page</th><th>Last seen</th><th /></tr></thead>
          <tbody className="divide-y divide-white/5">
            {items.map((v) => (
              <tr key={v.ip} data-testid={`visitor-row-${v.ip}`}>
                <td className="py-3 font-mono text-xs text-zinc-300">{v.ip} {v.suspicious && <span className="ml-1 rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] text-red-400">suspicious</span>}</td>
                <td className="pr-3 text-xs" data-testid={`visitor-location-${v.ip}`}><Location v={v} /></td>
                <td>{v.hits}</td><td className="capitalize text-zinc-400">{v.device}</td>
                <td className="max-w-[160px] truncate text-zinc-400">{v.last_path}</td>
                <td className="text-xs text-zinc-500">{new Date(v.last_seen).toLocaleString()}</td>
                <td className="text-right">{v.blocked ? <span className="text-xs text-red-400">Blocked</span> : <Btn variant="danger" onClick={() => block(v.ip)} data-testid={`block-${v.ip}`}>Block</Btn>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {d && !items.length && <p className="py-4 text-sm text-zinc-600">No visitors yet.</p>}
        {!d && <p className="py-4 text-sm text-zinc-600">Loading visitors…</p>}
      </div>
      {d && <Pager page={d.page} pages={d.pages} total={d.total} limit={limit}
        onPage={setPage} onLimit={(n) => { setLimit(n); setPage(1); }} />}
    </Card>
  );
};
